import {
  useRef,
  useEffect,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type RefCallback,
} from 'react';
import { Settings } from 'react-feather';
import Select from 'react-select';
import styled from '@emotion/styled';
import { useFloating, useInteractions, useClick, useDismiss } from '@floating-ui/react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { ChartData } from '@trinity/chat';
import { ChartThemePicker } from '@trinity/chat-components/components/ChartTheming';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context';
import { UpdateChartPreferencesContext } from '@trinity/chat-components/containers/ChartPreferences/context';
import type { BrandThemingContextValue } from '../../containers/Branding/interfaces';
import * as Presentation from '../Chat.Filters/Presentation';
import Input from '../Input';
import { ChartFilterButton } from './Chat.Chart.Filter.Button';
import { useChartFilters, useChartFilterDispatch, type FilterRow } from './Chat.Chart.Filter.Context';
import { ChartFilterMenu } from './Chat.Chart.Filter.Menu';
import { useActiveChartFiltersCount } from './Chat.Chart.Filter.utils';
import {
  convertToChartDataPoints,
  convertChartPayload,
  convertChartData,
  applyFilteredDataToChart,
  type ChartPayloadData,
} from './Chat.Chart.Settings.utils';
import type { ChartType } from './Chat.Chart.utils';
import { mapChartTypeToBackend } from './Chat.Chart.utils';
import {
  ChartGroupByContext,
  ChartInfoContext,
  ChartTypeContext,
  ChartRecordIdContext,
  ChartThemeIdContext,
} from './context';

function ChartFilterSection({
  setFilterReference,
  setFilterFloating,
  getFilterReferenceProps,
  filterOpen,
  setFilterOpen,
  isFilterSupported,
  activeFiltersCount,
  filterFloatingStyles,
  getFilterFloatingProps,
  chart,
  filters,
  originalData,
}: {
  setFilterReference: RefCallback<Element>;
  setFilterFloating: RefCallback<HTMLElement>;
  getFilterReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  filterOpen: boolean;
  setFilterOpen: (v: boolean | ((o: boolean) => boolean)) => void;
  isFilterSupported: boolean;
  activeFiltersCount: number;
  filterFloatingStyles: CSSProperties;
  getFilterFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  chart: ChartData;
  filters: FilterRow[];
  originalData: ReturnType<typeof convertToChartDataPoints>;
}) {
  return (
    <>
      <span
        ref={setFilterReference}
        {...getFilterReferenceProps()}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <ChartFilterButton
          onClick={() => setFilterOpen((open) => !open)}
          disabled={!isFilterSupported}
          filtersCount={activeFiltersCount}
        />
      </span>
      {filterOpen && isFilterSupported && (
        <PopperRoot ref={setFilterFloating} style={filterFloatingStyles} {...getFilterFloatingProps()}>
          <PopperHeader>Chart Filters</PopperHeader>
          <PopperContent>
            <ChartFilterMenu chartInfo={{ ...chart, filters }} originalData={originalData} />
          </PopperContent>
        </PopperRoot>
      )}
    </>
  );
}

function ChartSettingsMenu({
  isOpen,
  setFloating,
  floatingStyles,
  getFloatingProps,
  selectedChartType,
  setSelectedChartType,
  selectedThemeId,
  setSelectedThemeId,
  brandThemingData,
  brandThemingQuery,
}: {
  isOpen: boolean;
  setFloating: RefCallback<HTMLElement>;
  floatingStyles: CSSProperties;
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  selectedChartType: string;
  setSelectedChartType: (v: string) => void;
  selectedThemeId: number | null;
  setSelectedThemeId: (v: number | null) => void;
  brandThemingData: BrandThemingContextValue['data'];
  brandThemingQuery: BrandThemingContextValue['query'];
}) {
  if (!isOpen) return null;
  const groupingDisabled =
    selectedChartType !== 'stacked-bar' && selectedChartType !== 'bar' && selectedChartType !== 'pie';
  return (
    <Root ref={setFloating} style={floatingStyles} {...getFloatingProps()}>
      <Header>
        <HeaderLabel>Chart Settings</HeaderLabel>
      </Header>
      <Filters>
        <ChartTypeController selectedChartType={selectedChartType} onChange={setSelectedChartType} />

        <LabelContainer>
          <Label>Group small values into &quot;Others&quot;</Label>
          <SubLabel> Only applies when chart data contains small categories </SubLabel>
        </LabelContainer>

        <GroupController disabled={groupingDisabled} />

        {groupingDisabled && (
          <ErrorContainer>
            <StyledErrorIcon />
            <ErrorMessage>
              {' '}
              &quot;Others grouping&quot; is not available for this chart type or data set.
            </ErrorMessage>
          </ErrorContainer>
        )}
      </Filters>

      <Divider />

      <ChartThemePicker
        selectedThemeId={selectedThemeId ?? null}
        onThemeSelect={setSelectedThemeId}
        categories={brandThemingData?.categories ?? []}
        isLoading={brandThemingQuery?.isLoading ?? false}
      />
    </Root>
  );
}

export function ChartFilterPane() {
  const [isOpen, setIsOpen] = useState(false);
  const [chart, setChart] = useContext(ChartInfoContext);
  const [selectedChartType, setSelectedChartType] = useContext(ChartTypeContext);
  const [selectedThemeId, setSelectedThemeId] = useContext(ChartThemeIdContext);
  const { data: brandThemingData, query: brandThemingQuery } = useContext(BrandThemingContext);
  const [filterOpen, setFilterOpen] = useState(false);

  // Supported chart types for V1 filter panel
  const SUPPORTED_FILTER_CHART_TYPES_V1 = ['pie', 'donut', 'bar', 'stacked-bar'];

  const isFilterSupported = SUPPORTED_FILTER_CHART_TYPES_V1.includes(selectedChartType);

  // FILTER MENU FLOATING LOGIC
  const {
    refs: { setReference: setFilterReference, setFloating: setFilterFloating },
    floatingStyles: filterFloatingStyles,
    context: filterContext,
  } = useFloating({
    open: filterOpen,
    onOpenChange: setFilterOpen,
    placement: 'bottom-end',
  });

  const filterClick = useClick(filterContext);
  const filterDismiss = useDismiss(filterContext);
  const { getFloatingProps: getFilterFloatingProps, getReferenceProps: getFilterReferenceProps } =
    useInteractions([filterClick, filterDismiss]);

  // SETTINGS MENU FLOATING LOGIC
  const {
    context,
    refs: { setReference: setSettingsReference, setFloating: setSettingsFloating },
    floatingStyles,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-end',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const originalDataRef = useRef<ChartPayloadData>(chart.chart.chart.data);
  const [originalDataPoints, setOriginalDataPoints] = useState(() =>
    convertToChartDataPoints(chart.chart.chart.data)
  );

  // Declared before the effect so the closure captures their latest values.
  const dispatch = useChartFilterDispatch();
  const chartFiltersMap = useChartFilters();

  const prevChartTypeRef = useRef(selectedChartType);

  // On type switch: convert originalDataRef to the new format and transfer active filters.
  useEffect(() => {
    const prev = prevChartTypeRef.current;
    const next = selectedChartType;
    prevChartTypeRef.current = next;

    if (prev === next) return;

    const converted = convertChartData(prev, next, originalDataRef.current);
    if (converted !== originalDataRef.current) {
      originalDataRef.current = converted;
      setOriginalDataPoints(convertToChartDataPoints(converted));
    }

    const externalId = chart.widgetId ?? chart.quantItemId;
    const oldChartId = `${externalId}_${prev}`;
    const newChartId = `${externalId}_${next}`;
    const filtersToTransfer = chartFiltersMap[oldChartId] ?? [];
    if (filtersToTransfer.length > 0) {
      dispatch({ type: 'UPDATE_FILTERS', chartId: newChartId, filters: filtersToTransfer });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChartType]);

  const chartType: string = chart?.chart?.type || 'unknown';
  const externalId = chart.widgetId ?? chart.quantItemId;
  const chartId = `${externalId}_${chartType}`;

  // Get the active filters count for this chart
  const activeFiltersCount = useActiveChartFiltersCount(chartId);

  const filters = chartFiltersMap[chartId] ?? [];

  const handleFiltersChange = (filters: FilterRow[]) => {
    const originalData = originalDataRef.current;
    const chartTypeLocal: string = chart.chart.type;
    applyFilteredDataToChart(chartTypeLocal, originalData, filters, setChart);
  };

  useEffect(() => {
    if (filters && filters.length > 0) {
      handleFiltersChange(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, chartId, chartType]);

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss]);

  return (
    <TopRow>
      <ChartFilterSection
        setFilterReference={setFilterReference}
        setFilterFloating={setFilterFloating}
        getFilterReferenceProps={getFilterReferenceProps}
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        isFilterSupported={isFilterSupported}
        activeFiltersCount={activeFiltersCount}
        filterFloatingStyles={filterFloatingStyles}
        getFilterFloatingProps={getFilterFloatingProps}
        chart={chart}
        filters={filters}
        originalData={originalDataPoints}
      />

      {/* SETTINGS BUTTON */}
      <Tooltip title="Chart settings">
        <span
          ref={setSettingsReference}
          {...getReferenceProps()}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <StyledSettingsIcon />
        </span>
      </Tooltip>

      {/* SETTINGS MENU */}
      <ChartSettingsMenu
        isOpen={isOpen}
        setFloating={setSettingsFloating}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        selectedChartType={selectedChartType}
        setSelectedChartType={setSelectedChartType}
        selectedThemeId={selectedThemeId}
        setSelectedThemeId={setSelectedThemeId}
        brandThemingData={brandThemingData}
        brandThemingQuery={brandThemingQuery}
      />
    </TopRow>
  );
}

const Divider = styled.div({
  height: '1px',
  backgroundColor: '#e0e0e0',
  margin: '16px 0',
});

const TopRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

function ChartTypeController({
  selectedChartType,
  onChange,
}: {
  selectedChartType: string | null;
  onChange: (value: string | null) => void;
}) {
  const [, setChart] = useContext(ChartInfoContext);
  const chartRecordId = useContext(ChartRecordIdContext);
  const overrideTypesRef = useRef<string[]>([]);
  const theme = useTheme();

  const updateChartRecordOverride = useContext(UpdateChartPreferencesContext);

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const filteredOptions = useMemo(() => {
    const singleOptions = [
      { value: 'bar', label: 'Bar' },
      { value: 'pie', label: 'Pie' },
      { value: 'stacked-bar', label: 'Singular Stacked Bar' },
    ];

    const multipleOptions = [
      { value: 'multiple-stacked-bar', label: 'Multiple Stacked Bar' },
      { value: 'multiple-clustered-bar', label: 'Multiple Clustered Bar' },
    ];

    // Line charts only map to themselves
    const lineOptions = [
      { value: 'line', label: 'Line' },
      { value: 'multiple-line', label: 'Multiple Line' },
    ];

    if (lineOptions.some((option) => option.value === selectedChartType)) {
      return lineOptions.filter((option) => option.value === selectedChartType);
    }
    if (singleOptions.some((option) => option.value === selectedChartType)) {
      return singleOptions;
    } else return multipleOptions;
  }, [selectedChartType]);
  const handleChange = (option: { value: string }) => {
    const newChartType = option.value;

    // Record the chart type override
    const recordChartOverride = async () => {
      if (chartRecordId && selectedChartType !== newChartType) {
        try {
          const mappedChartType = mapChartTypeToBackend(newChartType);
          if (mappedChartType) {
            overrideTypesRef.current = [mappedChartType];
            await updateChartRecordOverride({
              recordId: chartRecordId,
              overrideChartTypes: overrideTypesRef.current as ChartType[],
            });
          }
        } catch (error) {
          console.warn('Failed to record chart type override:', error);
        }
      }
    };

    setChart((prev) => ({
      ...prev,
      chart: {
        ...prev.chart,
        type: newChartType as ChartData['chart']['type'],
        chart: convertChartPayload(selectedChartType, newChartType, prev.chart.chart),
      },
    }));

    onChange(newChartType);
    recordChartOverride().catch(() => undefined);
  };

  return (
    <ChartTypeContainer>
      <Label>Chart Type</Label>
      <Select
        options={filteredOptions}
        id="chart-type"
        value={filteredOptions.find((option) => option.value === selectedChartType)}
        onChange={handleChange}
        styles={selectStyles}
        theme={selectTheme}
      />
    </ChartTypeContainer>
  );
}

function GroupController({ disabled }: { disabled: boolean }) {
  const [groupBy, setGroupBy] = useContext(ChartGroupByContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGroupBy(+value);
  };

  return (
    <GroupContainer>
      <Label>Show Top N Categories:</Label>
      <Input
        id="group-values-below"
        placeholder="Input Number"
        type="number"
        value={groupBy || ''}
        disabled={disabled}
        onChange={handleInputChange}
      />
    </GroupContainer>
  );
}

const PopperRoot = styled.div(({ theme }) => ({
  background: theme?.palette?.white?.main || '#fff',
  border: `1px solid ${theme?.palette?.gray?.light1 || '#e0e0e0'}`,
  borderRadius: 6,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  minWidth: 280,
  zIndex: 1000,
  marginTop: 4,
}));

const PopperHeader = styled.div(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  padding: '12px 16px 8px 16px',
  borderBottom: `1px solid ${theme?.palette?.gray?.light2 || '#f0f0f0'}`,
}));

const PopperContent = styled.div({
  padding: '12px 16px',
});

const Root = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.gray.light1}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  padding: '12px',
  minWidth: 280,
  zIndex: 1000,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const Filters = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const StyledErrorIcon = styled(ErrorOutlineIcon)({
  color: 'red',
  fontSize: '12px',
  marginLeft: '5px',
});

const ErrorMessage = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  color: 'red',
  fontSize: '10px',
}));

const ErrorContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
});

const ChartTypeContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '5px',
  marginBottom: '10px',
});

const GroupContainer = styled.div({
  display: 'flex',
  whiteSpace: 'nowrap',
  alignItems: 'center',
  gap: '2px',
  marginBottom: '10px',
});

const Label = styled.label(({ theme }) => ({
  whiteSpace: 'nowrap',
  fontFamily: theme.fonts.semiBold,
  fontSize: '14px',
}));

const SubLabel = styled.label(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: '10px',
  color: theme.palette.gray.light1,
}));

const LabelContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  marginBottom: '10px',
});

const StyledSettingsIcon = styled(Settings)(({ theme }) => ({
  fontSize: '12px !important',
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));
